import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { BoardPitchModal } from '../components/shell/BoardPitchModal';
import { Button } from '../components/ui';
import { Printer, Copy, CheckCircle2, Shield, ArrowRight, DollarSign, Lock, FileText, Share2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

export const BoardMemoPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isPitchModalOpen, setIsPitchModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (searchParams.get('print') === 'true') {
      const timer = setTimeout(() => {
        window.print();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.origin + '/board-memo');
    setIsCopied(true);
    toast.success('Memo link copied to clipboard!');
    setTimeout(() => setIsCopied(false), 2500);
  };

  const todayFormatted = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date());

  return (
    <div className="min-h-screen font-sans bg-white dark:bg-black text-black dark:text-white transition-colors duration-300 selection:bg-brand-primary/20">
      <SEO
        title="1-Page Board Pitch & Executive Memo | OrgSets"
        description="Official 1-page executive memorandum for club, league, and HOA boards evaluating the OrgSets platform. Includes cost analysis, treasurer banking safeguards, and compliance overview."
      />

      <div className="print:hidden">
        <PublicHeader />
      </div>

      <main className="pt-28 pb-20 px-4 md:px-8 print:p-0 print:pt-4">
        {/* Top Control Bar (Hidden when printing) */}
        <div className="max-w-4xl mx-auto mb-8 print:hidden">
          <div className="border-[4px] border-black dark:border-white p-4 bg-white dark:bg-zinc-950 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <span className="text-[10px] font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600] block mb-0.5">
                Executive Resource Pack
              </span>
              <h2 className="text-lg font-black font-display uppercase tracking-tight">
                1-Page Board & Treasurer Brief
              </h2>
            </div>

            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              <Button
                variant="primary"
                onClick={handlePrint}
                className="flex-1 sm:flex-initial rounded-none bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-wider h-11 px-5 flex items-center justify-center gap-2 hover:bg-[#0044FF] hover:text-white transition-colors border-[3px] border-black dark:border-white"
              >
                <Printer size={15} />
                <span>Print / Save PDF</span>
              </Button>
              <Button
                variant="secondary"
                onClick={() => setIsPitchModalOpen(true)}
                className="flex-1 sm:flex-initial rounded-none border-[3px] border-black dark:border-white text-black dark:text-white font-black uppercase text-xs tracking-wider h-11 px-4 flex items-center justify-center gap-2 hover:bg-[#FFD600] hover:text-black transition-colors"
              >
                <Copy size={15} />
                <span>Copy Pitch</span>
              </Button>
              <button
                type="button"
                onClick={handleCopyLink}
                title="Copy shareable link"
                className="h-11 px-3 border-[3px] border-black dark:border-white bg-gray-100 dark:bg-zinc-900 text-black dark:text-white hover:bg-black hover:text-white transition-colors flex items-center justify-center"
              >
                {isCopied ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Share2 size={16} />}
              </button>
            </div>
          </div>
        </div>

        {/* The Printable Executive Memorandum */}
        <article className="max-w-4xl mx-auto border-[6px] md:border-[8px] border-black dark:border-white bg-white dark:bg-black p-6 md:p-12 shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#ffffff] print:border-[3px] print:border-black print:shadow-none print:p-6 print:text-black print:bg-white">
          
          {/* Memorandum Header Strip */}
          <div className="border-b-[4px] border-black dark:border-white print:border-black pb-6 mb-8">
            <div className="flex items-center justify-between gap-4 mb-4">
              <span className="font-mono text-xs font-black uppercase tracking-widest bg-black text-white dark:bg-white dark:text-black print:bg-black print:text-white px-3 py-1">
                EXECUTIVE MEMORANDUM
              </span>
              <span className="font-mono text-xs font-bold text-gray-500 uppercase tracking-widest">
                BOARD & TREASURER BRIEF
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight leading-none mb-6">
              Platform Modernization Proposal: OrgSets
            </h1>

            {/* Meta Table */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-xs border-[2px] border-black dark:border-white print:border-black p-4 bg-gray-50 dark:bg-zinc-950 print:bg-white">
              <div>
                <span className="text-gray-500 font-bold uppercase block text-[10px]">TO:</span>
                <span className="font-bold text-black dark:text-white print:text-black">Board of Directors, President & Treasurer</span>
              </div>
              <div>
                <span className="text-gray-500 font-bold uppercase block text-[10px]">FROM:</span>
                <span className="font-bold text-black dark:text-white print:text-black">Technology & Operations Committee</span>
              </div>
              <div>
                <span className="text-gray-500 font-bold uppercase block text-[10px]">DATE:</span>
                <span className="font-bold text-black dark:text-white print:text-black">{todayFormatted}</span>
              </div>
              <div>
                <span className="text-gray-500 font-bold uppercase block text-[10px]">RECOMMENDATION:</span>
                <span className="font-bold text-emerald-700 dark:text-emerald-400 print:text-black">Approve 30-Day No-Cost Pilot (Up to 50 Members)</span>
              </div>
            </div>
          </div>

          {/* Section 1: Executive Summary */}
          <section className="mb-8">
            <h2 className="text-xs font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600] print:text-black mb-2 flex items-center gap-2">
              <span>01 // EXECUTIVE SUMMARY & CHALLENGE</span>
            </h2>
            <p className="text-sm md:text-base font-medium leading-relaxed text-gray-800 dark:text-gray-200 print:text-black">
              Our organization currently relies on a disjointed assembly of consumer applications (e.g., Mailchimp for email blasts, Venmo/spreadsheets for dues, Google Drive for bylaws, and group text threads for announcements). This creates three major organizational vulnerabilities:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-300 print:text-black pl-4 border-l-4 border-black dark:border-white print:border-black">
              <li><strong>Financial Friction:</strong> Manual payment tracking, spreadsheet errors, and delayed dues reconciliation for our Treasurer.</li>
              <li><strong>Subscription Creep:</strong> Paying ~$140–$200/month across disjointed software accounts with separate renewal cycles.</li>
              <li><strong>Governance & Liability:</strong> Shared passwords, disorganized liability waivers, and vulnerable member contact records.</li>
            </ul>
          </section>

          {/* Section 2: Financial ROI & Cost Comparison */}
          <section className="mb-8">
            <h2 className="text-xs font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600] print:text-black mb-2 flex items-center gap-2">
              <DollarSign size={14} />
              <span>02 // FINANCIAL ROI: $1,500+ ANNUAL SAVINGS</span>
            </h2>

            <div className="overflow-x-auto border-[3px] border-black dark:border-white print:border-black mb-4">
              <table className="w-full text-left font-mono text-xs">
                <thead className="bg-black text-white dark:bg-white dark:text-black print:bg-black print:text-white uppercase font-bold">
                  <tr>
                    <th className="p-3">Operational Need</th>
                    <th className="p-3">Current Fragmented Stack</th>
                    <th className="p-3 bg-[#0044FF] text-white print:bg-black">OrgSets Unified Command</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/20 dark:divide-white/20 print:divide-black">
                  <tr className="bg-white dark:bg-black print:bg-white">
                    <td className="p-3 font-bold">Member Broadcasts & Email</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400 print:text-black">Mailchimp (~$45/mo)</td>
                    <td className="p-3 font-bold text-emerald-700 dark:text-emerald-400 print:text-black">Included ($0/mo)</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-zinc-950 print:bg-white">
                    <td className="p-3 font-bold">Dues & Ledger Tracking</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400 print:text-black">QuickBooks / Venmo (~$50/mo)</td>
                    <td className="p-3 font-bold text-emerald-700 dark:text-emerald-400 print:text-black">$0/mo + $0.50/tx flat fee</td>
                  </tr>
                  <tr className="bg-white dark:bg-black print:bg-white">
                    <td className="p-3 font-bold">Bylaws, Waivers & File Vault</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400 print:text-black">Google Workspace (~$36/mo)</td>
                    <td className="p-3 font-bold text-emerald-700 dark:text-emerald-400 print:text-black">Included ($0/mo)</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-zinc-950 print:bg-white">
                    <td className="p-3 font-bold">Urgent Alerts & Pulse SMS</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400 print:text-black">Text Gateway (~$20/mo)</td>
                    <td className="p-3 font-bold text-emerald-700 dark:text-emerald-400 print:text-black">Included ($0/mo)</td>
                  </tr>
                  <tr className="bg-black/5 dark:bg-white/10 print:bg-gray-100 font-black text-sm">
                    <td className="p-3">TOTAL ANNUAL COST</td>
                    <td className="p-3 text-rose-600 print:text-black">~$1,812 / year</td>
                    <td className="p-3 text-emerald-700 dark:text-emerald-400 print:text-black">$0 Fixed / ~80% Savings</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-500/60 p-4 text-xs font-mono text-emerald-950 dark:text-emerald-200 print:text-black print:border-black print:bg-white">
              <span className="font-black uppercase tracking-wider block mb-1">
                ✓ Treasurer Banking Safeguards
              </span>
              <span>
                All dues payments process directly through Stripe Connect into our organization's existing checking account. Funds are never held in an escrow intermediary. Downloadable real-time ledger simplifies our annual 990 / IRS filing.
              </span>
            </div>
          </section>

          {/* Section 3: Governance, Privacy & Security */}
          <section className="mb-8">
            <h2 className="text-xs font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600] print:text-black mb-3 flex items-center gap-2">
              <Shield size={14} />
              <span>03 // GOVERNANCE, LIABILITY & COMPLIANCE</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-xs">
              <div className="border-[2px] border-black dark:border-white print:border-black p-4 bg-gray-50 dark:bg-zinc-950 print:bg-white">
                <div className="font-black uppercase tracking-wider mb-1 flex items-center gap-1.5 text-black dark:text-white print:text-black">
                  <Lock size={14} className="text-brand-primary" />
                  Role-Based Security
                </div>
                <p className="text-gray-600 dark:text-gray-400 print:text-black leading-relaxed">
                  Distinct access tiers for President, Treasurer, Board Admins, and Members. Eliminates risky shared passwords.
                </p>
              </div>

              <div className="border-[2px] border-black dark:border-white print:border-black p-4 bg-gray-50 dark:bg-zinc-950 print:bg-white">
                <div className="font-black uppercase tracking-wider mb-1 flex items-center gap-1.5 text-black dark:text-white print:text-black">
                  <FileText size={14} className="text-brand-primary" />
                  Digital Vault
                </div>
                <p className="text-gray-600 dark:text-gray-400 print:text-black leading-relaxed">
                  Encrypted repository for bylaws, meeting minutes, and signed liability waivers with verifiable audit logging.
                </p>
              </div>

              <div className="border-[2px] border-black dark:border-white print:border-black p-4 bg-gray-50 dark:bg-zinc-950 print:bg-white">
                <div className="font-black uppercase tracking-wider mb-1 flex items-center gap-1.5 text-black dark:text-white print:text-black">
                  <Shield size={14} className="text-brand-primary" />
                  Strict Member Privacy
                </div>
                <p className="text-gray-600 dark:text-gray-400 print:text-black leading-relaxed">
                  Zero commercial advertisements, zero algorithm trackers, and zero selling of member emails or phone numbers.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Recommended Board Motion */}
          <section className="mb-8 border-[3px] border-black dark:border-white print:border-black p-5 bg-[#FFD600]/10 dark:bg-yellow-950/20 print:bg-white">
            <h2 className="text-xs font-mono font-black uppercase tracking-widest text-black dark:text-white print:text-black mb-2">
              04 // PROPOSED ACTION & MEETING MINUTES MOTION
            </h2>
            <p className="text-xs font-mono text-gray-700 dark:text-gray-300 print:text-black mb-3">
              We recommend approving a 30-day trial pilot for up to 50 active members. Ready-to-read board motion text:
            </p>
            <blockquote className="border-l-4 border-black dark:border-white print:border-black pl-4 py-1 italic font-serif text-sm md:text-base text-black dark:text-white print:text-black font-bold">
              "I move that the board authorize a 30-day operational evaluation of the OrgSets platform for member communications and dues processing at zero software cost to the organization."
            </blockquote>
          </section>

          {/* Bottom Action Footer */}
          <div className="pt-6 border-t-[4px] border-black dark:border-white print:border-black flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="font-mono text-xs text-gray-500">
              <span>Platform Reference: </span>
              <a href="https://orgsets.com" className="font-bold underline text-black dark:text-white print:text-black">
                https://orgsets.com
              </a>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto print:hidden">
              <Link 
                to="/plans" 
                className="w-full sm:w-auto h-12 inline-flex items-center justify-center gap-2 rounded-none bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-wider px-6 hover:bg-[#0044FF] hover:text-white transition-colors border-[3px] border-black dark:border-white shadow-[4px_4px_0px_#000000]"
              >
                <span>Launch Free 50-Member Hub</span>
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </article>
      </main>

      <div className="print:hidden">
        <PublicFooter />
      </div>

      <BoardPitchModal
        isOpen={isPitchModalOpen}
        onClose={() => setIsPitchModalOpen(false)}
      />
    </div>
  );
};
